import { useEffect } from "react";

export function useTabTracker({
    trackingLsKey,
    onAllTabsClosed,
    disabled = false,
}: {
    trackingLsKey: string; // should be unique key per hook usage
    onAllTabsClosed: () => void;
    disabled?: boolean;
}) {
    useEffect(() => {
        if (disabled) return;

        initTabCount(trackingLsKey);
        const cleanupBeforeUnloadListener = handleTabClose(trackingLsKey, onAllTabsClosed);

        return () => {
            cleanupBeforeUnloadListener();
            decreaseTabCount(trackingLsKey); // handling rerenders if any occurs, mostly during local development
        };
    }, [onAllTabsClosed, trackingLsKey, disabled]);
}

function initTabCount(lsKey: string): void {
    const currentTabCount = getCurrentTabCount(lsKey);
    localStorage.setItem(lsKey, (currentTabCount + 1).toString());
}

function handleTabClose(lsKey: string, onAllTabsClosed: () => void): () => void {
    const beforeUnloadListener = () => {
        if (decreaseTabCount(lsKey) === 0) {
            onAllTabsClosed();
        }
    }

    window.addEventListener("beforeunload", beforeUnloadListener);

    return () => {
        window.removeEventListener("beforeunload", beforeUnloadListener);
    };
}

function getCurrentTabCount(lsKey: string) {
    return parseInt(localStorage.getItem(lsKey) || "0", 10);
}

function decreaseTabCount(lsKey: string): number {
    const currentTabCount = getCurrentTabCount(lsKey);
    const newTabCount = Math.max(currentTabCount - 1, 0);
    localStorage.setItem(lsKey, newTabCount.toString());
    return newTabCount;
}
